// src/utils/threadsSplitUtils.js

// ====== Распознавание языков ======
const SCRIPTS = {
    latin: /[A-Za-z\u00C0-\u024F\u1E00-\u1EFF]/,
    cyril: /[\u0400-\u04FF]/,
    kazakh: /[ӘәҒғҚқҢңӨөҰұҮүҺһІі]/, // специфические казахские буквы
    arabic: /[\u0600-\u06FF]/,
    hebrew: /[\u0590-\u05FF]/,
};

// ====== Определение доминирующего алфавита ======
function dominantScript(text) {
    const counts = { latin:0, cyril:0, kazakh:0, arabic:0, hebrew:0 };
    for (const ch of text) {
        for (const [name, re] of Object.entries(SCRIPTS)) {
            if (re.test(ch)) { counts[name]++; break; }
        }
    }
    const entries = Object.entries(counts);
    const total = entries.reduce((s, [,v]) => s+v, 0);
    if (!total) return null;
    entries.sort((a,b)=>b[1]-a[1]);
    return { script: entries[0][0], ratio: entries[0][1]/total };
}

// ====== Разделение текста по языкам ======
export function splitByLanguageBlocks(raw, {
    minSwitchRatio = 0.70,
    minBlockChars = 120,
    noiseTolerance = 0.18,
} = {}) {
    if (!raw) return [];

    const text = String(raw).replace(/\r/g,'').replace(/\n{2,}/g,'\n').trim();
    const sentences = text
        .split(/(?<=[.!?…])\s+/)
        .filter(Boolean);

    const blocks = [];
    let cur = { lang: null, text: '' };
    let curCount = 0;
    let curScript = null;

    const detectScript = s => {
        const d = dominantScript(s);
        if (!d) return null;
        if (SCRIPTS.kazakh.test(s)) return 'kazakh';
        return d.ratio >= noiseTolerance ? d.script : null;
    };

    for (let i = 0; i < sentences.length; i++) {
        const s = sentences[i];
        const sc = detectScript(s);

        if (!cur.lang) {
            cur.lang = sc || null;
            curScript = sc;
            cur.text += (cur.text ? ' ' : '') + s;
            curCount += s.length;
            continue;
        }

        if (!sc || sc === curScript) {
            cur.text += (cur.text ? ' ' : '') + s;
            curCount += s.length;
            continue;
        }

        const d = dominantScript(s);
        if (!d || d.ratio < minSwitchRatio || curCount < minBlockChars) {
            cur.text += (cur.text ? ' ' : '') + s;
            curCount += s.length;
            continue;
        }

        if (cur.text.trim())
            blocks.push({ lang: curScript || cur.lang || 'und', text: cur.text.trim() });

        cur = { lang: sc, text: s };
        curScript = sc;
        curCount = s.length;
    }

    if (cur.text.trim())
        blocks.push({ lang: curScript || cur.lang || 'und', text: cur.text.trim() });

    const merged = [];
    for (const b of blocks) {
        const last = merged[merged.length-1];
        if (last && last.lang === b.lang) last.text += ' ' + b.text;
        else merged.push(b);
    }

    return merged;
}

// ====== Разделение по лимиту символов (500) ======
export function splitForThreads(text, limit = 500) {
    if (!text) return [];

    let rest = String(text)
        .replace(/\r/g, '')
        .replace(/\n{2,}/g, '\n') // убираем пустые строки, сохраняем красные строки
        .trim();

    const out = [];
    const STRONG_BREAK_MIN = Math.floor(limit * 0.8);
    const COMMA_BREAK_MIN  = Math.floor(limit * 0.9);

    const lastMatchEnd = (re, s, max) => {
        let m, end = -1;
        re.lastIndex = 0;
        while ((m = re.exec(s)) && m.index <= max) end = m.index + m[0].length;
        return end;
    };

    const ltrimPunct = s => s.replace(/^[)\]»"'“”,—-]+/, '').replace(/^\s+/, '');

    while (rest.length > limit) {
        let cut = -1;

        const endSentence = lastMatchEnd(/[.!?…]+["'»)\]]*(?=\s|$)/g, rest, limit);
        if (endSentence > 0) cut = endSentence;

        if (cut < STRONG_BREAK_MIN) {
            let md = rest.lastIndexOf(' — ', limit);
            if (md < 0) md = rest.lastIndexOf(' – ', limit);
            if (md < 0) md = rest.lastIndexOf(' - ', limit);
            const sc = rest.lastIndexOf(';', limit);
            const co = rest.lastIndexOf(':', limit);
            cut = Math.max(cut, md > 0 ? md + 3 : -1, sc, co);
            if (cut === sc || cut === co) cut += 1;
        }

        if (cut < COMMA_BREAK_MIN) {
            const commas = [...rest.matchAll(/, /g)].map(m => m.index);
            const valid = commas
                .filter(i => i < limit)
                .reverse()
                .find(i => {
                    const nextWord = rest.slice(i + 2, i + 8).toLowerCase();
                    return !/^(and|or|и|но)\b/.test(nextWord);
                });
            if (valid !== undefined) cut = valid + 1;
        }

        if (cut < Math.floor(limit * 0.7)) {
            const sp = rest.lastIndexOf(' ', limit);
            if (sp > 0) cut = sp;
        }

        if (cut < 1) cut = limit;

        let head = rest.slice(0, cut).trimEnd();
        let tail = rest.slice(cut).trimStart();

        tail = ltrimPunct(tail);

        if (/^[a-zа-яё]/.test(tail)) {
            const prevDot = head.lastIndexOf('.');
            if (prevDot > 0 && head.length - prevDot < 100) {
                tail = head.slice(prevDot + 1).trim() + ' ' + tail;
                head = head.slice(0, prevDot + 1).trim();
            }
        }

        out.push(head);
        rest = tail;
    }

    if (rest) out.push(rest.trim());
    return out;
}

// ====== Объединённая мульти-язычная функция ======
export function splitMultilangForThreads(text, limit = 500) {
    const blocks = splitByLanguageBlocks(text);
    const parts = [];

    for (const b of blocks) {
        const chunks = splitForThreads(b.text, limit);
        for (let i = 0; i < chunks.length; i++) {
            parts.push({ lang: b.lang, text: chunks[i], i, n: chunks.length });
        }
    }
    return parts;
}

// ====== Доп. утилита для меток языков (если нужно в превью) ======
export function addLangPrefix(parts) {
    const seen = new Set();
    return parts.map(p => {
        if (!seen.has(p.lang)) {
            seen.add(p.lang);
            return { ...p, text: `${p.text}` };
        }
        return p;
    });
}
